import { FunctionComponent, useEffect, useMemo } from 'react'
import { Box } from '@mui/material'
import { LoginFormInputOptions } from '@dxp-module/login'
import { useNavigateWithPreview, usePlatformConfig } from '@dxp-module/common'
import MainHeader from '../../../common/components/mainHeader/MainHeader'
import VehicleBanner from '../../../common/components/vehicleBanner/VehicleBanner'
import ROUTES from '../../../common/enum/Routes'
import useCustomerDetails from '../../../common/hooks/queries/useCustomerDetails'
import collectedFilters from '../../../common/services/collectedFilters'
import ReturnToMainSiteLink from '../../../myAccount/components/ReturnToMainSiteLink'
import LoginContainer from '../loginContainer/LoginContainer'
import useBackClickHandler from '../../../common/hooks/useBackClickHandler'
import loginStyles from './login.styles'

type LoginProps = {
  hideSignup?: boolean
}

const Login: FunctionComponent<LoginProps> = ({ hideSignup }) => {
  const styles = loginStyles()
  const {
    filters: { token },
  } = collectedFilters()

  const { customer, error, loading } = useCustomerDetails(token)
  const formOptions: LoginFormInputOptions = useMemo(
    () =>
      customer?.email
        ? {
            emailAddress: {
              readonly: true,
              value: customer?.email,
            },
          }
        : {},
    [customer]
  )

  const {
    platformConfig: {
      myAccount: { isSeparateSignupLogin, showBannerBackLink },
    },
  } = usePlatformConfig()

  const backClickHandler = useBackClickHandler()
  const navigateWithPreview = useNavigateWithPreview()

  useEffect(() => {
    if (!token || error) {
      navigateWithPreview(ROUTES.INDEX)
    }
  }, [error, navigateWithPreview, token])

  if (!isSeparateSignupLogin && (!token || error || loading)) {
    return null
  }

  return (
    <Box className={styles.layoutContainer}>
      <ReturnToMainSiteLink />
      <Box data-test="login:container" className={styles.root}>
        <MainHeader
          headerTitle={
            isSeparateSignupLogin
              ? 'trans__myaccount__create_an_account__login__header_sign_up_or_login'
              : ''
          }
          backLinkText="trans__myaccount__headers__back_cta"
          backClickHandler={backClickHandler}
          hideBackCtaOnDesktop={!isSeparateSignupLogin}
          showBannerBackLink={!!showBannerBackLink}
        />
        <VehicleBanner
          pageName="login"
          title="trans__myaccount__login__title"
          description="trans__myaccount__login__desc"
          imageFilename={
            isSeparateSignupLogin ? 'MyAccountBanner' : 'VehicleBanner'
          }
        />
        <Box className={styles.container}>
          <LoginContainer
            formOptions={formOptions}
            className={styles.loginModule}
            hideSignup={hideSignup}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default Login
